import { DistributorsApiService } from "../api/DistributorsService";
import {
  GetDistributorDto,
  GetUserPermissionsDto,
  ProEntitiesTree,
  TreeNode,
} from "../api/index.defs";
import {
  AddEntity,
  IEntitiesTreeItem,
  IEntity,
  State,
  UpdateEntity,
} from "../models/entity.interface";
import { IResult } from "../models/results/result.interface";
import Role from "../models/role";
import { getServerRoleFromRole } from "../utils/authorizationUtil";
import logger from "../utils/logger";

const getOrgEntities = async (
  id: string,
  restricted?: "pro" | "non-pro"
): Promise<IEntity[] | null> => {
  logger.info(
    getOrgEntities.name,
    `Getting org ${id} ${restricted === "pro" ? "pro" : restricted === "non-pro" ? "patient" : ""
    } entities...`
  );
  try {
    const { response: resp, error } =
      restricted === "non-pro"
        ? await DistributorsApiService.distributorsGetPatientEntities({
          distId: +id,
        })
        : await DistributorsApiService.distributorsGetEntities({
          distId: +id,
        });

    if (error || !resp) {
      logger.error(getOrgEntities.name, "Invalid API response", {
        resp,
        error,
      });
      return null;
    }

    const entities = mapToEntities(resp);

    return entities;
  } catch (error) {
    logger.error(getOrgEntities.name, "Error getting Org entities.", error);
    return null;
  }
};

const getProEntitiesTree = async (
  id: string,
): Promise<ProEntitiesTree | null> => {
  logger.info(
    getProEntitiesTree.name,
    `Getting org ${id} pro entities...`
  );
  try {
    const { response: resp, error } = await DistributorsApiService.distributorsGetProfessionalEntities({
      distId: +id,
    })

    if (error || !resp || !resp.root) {
      logger.error(getProEntitiesTree.name, "Invalid API response", {
        resp,
        error,
      });
      return null;
    }

    return resp;
  } catch (error) {
    logger.error(getProEntitiesTree.name, "Error getting Pro entities.", error);
    return null;
  }
};

const getOrgEntitiesTree = async (
  id: string
): Promise<{
  tree: IEntitiesTreeItem[];
  totalEntities: number;
  totalUsers: number;
  totalActiveUsers: number;
} | null> => {
  logger.info(getOrgEntitiesTree.name, `Getting org ${id} entities tree...`);
  try {
    const {
      response: resp,
      error,
    } = await DistributorsApiService.distributorsGetOrganizationTree({
      distId: +id,
    });

    if (error || !resp?.root) {
      logger.error(getOrgEntitiesTree.name, "Invalid API response", {
        resp,
        error,
      });
      return null;
    }

    const tree = mapToEntitiesTree(resp.root);
    const { nodeCount: totalEntities, totalUsers, totalActiveUsers } = resp;

    return {
      totalEntities: totalEntities ?? 0,
      totalUsers: totalUsers ?? 0,
      totalActiveUsers: totalActiveUsers ?? 0,
      tree: [tree],
    };
  } catch (error) {
    logger.error(
      getOrgEntitiesTree.name,
      "Error getting Org entities tree.",
      error
    );
    return null;
  }
};

const addEntity = async (
  parentId: string,
  entity: AddEntity
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(
    addEntity.name,
    `Adding new entity to parent:${parentId}`,
    entity
  );

  try {
    const { name, email, phone, reference, state } = entity;
    const {
      response: resp,
      error,
    } = await DistributorsApiService.distributorsCreateDistributor({
      distributorDto: {
        parentDistId: +parentId,
        country: 1,
        state: state,
        distName: name,
        email: email,
        phoneNumber: phone,
        referenceNumber: reference,
      },
    });

    if (error || !resp) {
      logger.error(addEntity.name, "Invalid API response", { resp, error });
      return {
        success: false,
        errorCode: error?.errorCode?.toString(),
      };
    }

    // Added successfully
    return {
      success: true,
    };
  } catch (error) {
    logger.error(addEntity.name, "Error adding entity.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const updateEntity = async (entity: UpdateEntity): Promise<IResult<any>> => {
  logger.info(updateEntity.name, `Updating entity`, entity);

  try {
    const {
      id,
      name: distName,
      email,
      phone: phoneNumber,
      reference: referenceNumber,
    } = entity;

    const {
      response: resp,
      error,
    } = await DistributorsApiService.distributorsUpdateDistributor({
      distId: +id,
      distributorDto: {
        distName,
        phoneNumber,
        referenceNumber,
        email,
      },
    });

    if (error || !resp) {
      logger.error(updateEntity.name, "Invalid API response", { resp, error });
      return {
        success: false,
        errorCode: error?.errorCode?.toString(),
      };
    }

    // Updated successfully
    return {
      success: true,
    };
  } catch (error) {
    logger.error(updateEntity.name, "Error updating entity.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const deleteEntity = async (
  id: string
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(deleteEntity.name, `Deleting entity ${id}`);

  try {
    const {
      response: resp,
      error,
    } = await DistributorsApiService.distributorsDeleteDistributor({
      distId: +id,
    });

    if (error || !resp) {
      logger.error(deleteEntity.name, "Invalid API response", { resp, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    // Removed successfully
    return {
      success: true,
    };
  } catch (error) {
    logger.error(deleteEntity.name, "Error removing entity.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const getOrganiztionDefaultPermissions = async (
  id: string,
  role: Role
): Promise<IResult<GetUserPermissionsDto>> => {
  logger.info(
    getOrganiztionDefaultPermissions.name,
    `Getting organiztion default permissions for distId ${id} and role ${role}`
  );

  try {
    const {
      response: resp,
      error,
    } = await DistributorsApiService.distributorsGetRolePermissions({
      distId: +id,
      roleId: getServerRoleFromRole(role),
    });

    if (error || !resp) {
      logger.error(
        getOrganiztionDefaultPermissions.name,
        "Invalid API response",
        { resp, error }
      );
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
      data: resp,
    };
  } catch (error) {
    logger.error(
      deleteEntity.name,
      "Error getting organiztion default permissions.",
      error
    );
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const mapToEntities = (entitiesToMap: GetDistributorDto[] = []): IEntity[] => {
  return entitiesToMap.map((entity) => ({
    id: entity.id?.toString(),
    parentId: entity.parentDistId?.toString(),
    name: entity.name,
    email: entity.email,
    phone: entity.phoneNumber,
    rootName: entity.rootDistributorName,
    reference: entity.referenceNumber,
    country: entity.country,
    state: entity.state as State,
    totalUsers: entity.usersCount,
    activeUsers: entity.activeUsersCount,
  }));
};

const mapToEntitiesTree = (node: TreeNode<GetDistributorDto>) => {
  const { data: nodeData, children } = node;

  const treeItem: IEntitiesTreeItem = {
    id: nodeData.id?.toString(),
    parentId: nodeData.parentDistId?.toString(),
    name: nodeData.name,
    email: nodeData.email,
    phone: nodeData.phoneNumber,
    rootName: nodeData.rootDistributorName,
    reference: nodeData.referenceNumber,
    country: nodeData.country,
    state: nodeData.state as State,
    totalUsers: nodeData.usersCount,
    activeUsers: nodeData.activeUsersCount,
    childNodes: [],
    levelInTree: nodeData.levelInTree,
    maxEntitiesLevel: nodeData.maxLevelOfEntities,
  };

  if (children?.length) {
    for (let index = 0; index < children.length; index++) {
      const child = children[index];
      const treeEntity = mapToEntitiesTree(child);
      treeItem.childNodes?.push(treeEntity);
    }
  }

  return treeItem;
};

const organizationService = {
  getOrgEntities,
  getOrgEntitiesTree,
  addEntity,
  updateEntity,
  deleteEntity,
  getOrganiztionDefaultPermissions,
  getProEntitiesTree
};

export default organizationService;
